import Breadcrumb from "@components/layout/Breadcrumb";
import PageHeading from "@components/zenith/ui/PageHeading";
import { useAppContext } from "@context/AppContext";
import { CheckIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Step = {
  panel?: ReactNode;
  infoPanel?: ReactNode;
};

type WorkflowStepsProps = {
  title: string;
  /** The page this wizard belongs to, linked first in the breadcrumb. */
  parent: { label: string; to: string };
  selectedStep: number;
  onStepButtonClick: (step: number) => void;
  /** Stops done steps being revisited from the indicator. */
  disableNavigation?: boolean;
  /** The form steps, counted in "Step n of N". */
  steps: Step[];
  /** Shown when selectedStep is steps.length, e.g. after a submission. */
  completion?: Step;
};

const WorkflowSteps: FunctionComponent<WorkflowStepsProps> = ({
  title,
  parent,
  selectedStep,
  onStepButtonClick,
  disableNavigation = false,
  steps,
  completion,
}) => {
  const { t } = useTranslation();
  const { setInfoPanel } = useAppContext();
  const [stepNo, setStepNo] = useState(selectedStep || 0);
  const handleSelectStep = (i: number) => {
    setStepNo(i);
    onStepButtonClick(i);
  };

  useEffect(() => {
    setStepNo(selectedStep || 0);
  }, [selectedStep]);

  const isComplete = stepNo >= steps.length;
  const current = isComplete ? completion : steps[stepNo];

  // Callers rebuild `steps` on every render, so `current` is always new; setting
  // the panel re-renders them, and depending on `current` would loop.
  useEffect(() => {
    if (current?.infoPanel) {
      setInfoPanel(current.infoPanel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set once per step, as above
  }, [stepNo, setInfoPanel]);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Breadcrumb
          items={[{ label: parent.label, href: parent.to }, { label: title }]}
        />
        <PageHeading
          title={title}
          sub={
            isComplete
              ? null
              : t("zenith.workflows.step_of", {
                  step: stepNo + 1,
                  total: steps.length,
                })
          }
        />
      </div>
      <ol
        aria-label={t("zenith.workflows.progress") as string}
        className="flex items-center gap-2"
      >
        {steps.map((_, i) => {
          const isDone = isComplete || i < stepNo;
          const isCurrent = i === stepNo;
          const isLast = i === steps.length - 1;
          const circle = classNames(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold",
            {
              "border-green-600 bg-green-600 text-white": isDone,
              "border-primary-1 bg-primary-1 text-white": isCurrent,
              "border-gray-300 bg-white text-gray-500": !isDone && !isCurrent,
            },
          );
          const label = isDone ? (
            <>
              <CheckIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">
                {t("zenith.workflows.step_done", { step: i + 1 })}
              </span>
            </>
          ) : (
            i + 1
          );

          return (
            <li
              key={i}
              aria-current={isCurrent ? "step" : undefined}
              className={classNames("flex items-center gap-2", {
                "flex-1": !isLast,
              })}
            >
              {isDone && !isComplete && !disableNavigation ? (
                <button
                  type="button"
                  onClick={() => handleSelectStep(i)}
                  className={classNames(circle, "hover:bg-green-700")}
                >
                  {label}
                </button>
              ) : (
                <span className={circle}>{label}</span>
              )}
              {isLast ? null : (
                <span
                  aria-hidden="true"
                  className={classNames(
                    "h-0.5 flex-1",
                    isDone ? "bg-green-600" : "bg-gray-200",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
      <div className="flex min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-6">
        {current?.panel}
      </div>
    </div>
  );
};

export default WorkflowSteps;
